"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import type { Route } from "next";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { Loader2, Search, ArrowUpRight, CornerDownLeft } from "lucide-react";
import { Input } from "@eg/ui";
import {
  deriveBrand,
  deriveCategory,
  fetchProductSummaries,
  type ProductSummary,
} from "@/lib/discovery";
import { useTranslations } from "@/lib/i18n/I18nProvider";

export function SearchAutocomplete() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const t = useTranslations();
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState<ProductSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const initialQuery = useMemo(
    () => searchParams.get("q") ?? "",
    [searchParams],
  );

  useEffect(() => {
    setQuery(initialQuery);
  }, [initialQuery]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.trim().length < 2) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await fetchProductSummaries(query.trim(), 6);
        setSuggestions(res);
        setOpen(true);
        setActiveIndex(0);
      } finally {
        setLoading(false);
      }
    }, 220);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const buildUrl = (qValue: string, highlight?: string): Route => {
    const params = new URLSearchParams(searchParams.toString());
    if (qValue) params.set("q", qValue);
    else params.delete("q");
    if (highlight) params.set("highlight", highlight);
    else params.delete("highlight");
    const queryString = params.toString();
    return (queryString ? `/produkte?${queryString}` : "/produkte") as Route;
  };

  const submit = (target?: ProductSummary) => {
    const value = target ? target.title : query.trim();
    router.push(buildUrl(value, target?.slug));
    setOpen(false);
  };

  return (
    <div className="relative w-full max-w-xl" ref={containerRef}>
      <div className="relative">
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setOpen(suggestions.length > 0)}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setActiveIndex((prev) =>
                Math.min(prev + 1, Math.max(0, suggestions.length - 1)),
              );
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              setActiveIndex((prev) => Math.max(0, prev - 1));
            } else if (e.key === "Enter") {
              e.preventDefault();
              const target = suggestions[activeIndex];
              submit(target);
            }
          }}
          placeholder={t("discovery.searchAutocomplete.placeholder")}
          className="h-11 rounded-xl border-slate-300 bg-white pl-11 pr-10 text-slate-900 placeholder:text-slate-500 shadow-sm focus-visible:border-cyan-400 focus-visible:ring-2 focus-visible:ring-cyan-300"
        />
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        {loading ? (
          <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-brand-500" />
        ) : pathname === "/produkte" && query === initialQuery && query ? (
          <CornerDownLeft className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        ) : null}
      </div>

      {open && suggestions.length > 0 ? (
        <div className="absolute left-0 right-0 top-12 z-30 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl">
          <div className="border-b border-slate-200 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
            {t("discovery.searchAutocomplete.quickSuggestions")}
          </div>
          <ul role="listbox" className="divide-y divide-slate-100">
            {suggestions.map((s, idx) => {
              const isActive = idx === activeIndex;
              const brand = deriveBrand(s.title);
              const category = deriveCategory(s.slug, s.title);
              const categoryLabel =
                category === "vaporizer"
                  ? t("categories.vaporizer")
                  : category === "grinder"
                    ? t("categories.grinder")
                    : category === "papers"
                      ? t("categories.papers")
                      : category === "filterTips"
                        ? t("categories.filterTips")
                        : category === "cones"
                          ? t("categories.cones")
                          : category === "lighters"
                            ? t("categories.lighters")
                            : category === "storage"
                              ? t("categories.storage")
                              : category === "accessories"
                                ? t("categories.accessories")
                                : category === "cleaning"
                                  ? t("categories.cleaning")
                                  : category === "scales"
                                    ? t("categories.scales")
                                    : category === "humidor"
                                      ? t("categories.humidor")
                                      : t("categories.assortment");
              const text = s.title;
              const qLower = query.toLowerCase();
              const matchIndex = text.toLowerCase().indexOf(qLower);
              const before = matchIndex >= 0 ? text.slice(0, matchIndex) : text;
              const match =
                matchIndex >= 0
                  ? text.slice(matchIndex, matchIndex + query.length)
                  : "";
              const after =
                matchIndex >= 0 ? text.slice(matchIndex + query.length) : "";

              return (
                <li
                  key={s.id}
                  role="option"
                  aria-selected={isActive}
                  className={`flex cursor-pointer items-center justify-between gap-3 px-4 py-3 transition ${
                    isActive ? "bg-slate-100" : "hover:bg-slate-50"
                  }`}
                  onMouseEnter={() => setActiveIndex(idx)}
                  onClick={() => submit(s)}
                >
                  <div className="overflow-hidden">
                    <p className="truncate text-sm font-semibold text-gray-900">
                      {before}
                      <span className="rounded-sm bg-amber-100 px-0.5 text-amber-700">
                        {match}
                      </span>
                      {after}
                    </p>
                    <p className="text-xs text-gray-500">
                      {brand} · {categoryLabel}
                    </p>
                  </div>
                  <ArrowUpRight className="h-4 w-4 text-gray-400" />
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
